import {
  projectSponsoredSeatRuntimeEvidence,
  type SponsoredSeatEvidenceStatus,
} from './SponsoredSeatRuntimeEvidenceProjectionEngine';

export type SponsoredSeatCompletionDecision =
  | 'COMPLETE'
  | 'BLOCKED'
  | 'UNVERIFIED';

export interface SponsoredSeatCompletionCheck {
  id:
    | 'schema-activation'
    | 'reservation-authority'
    | 'claim-return-authority'
    | 'runtime-evidence-projection'
    | 'owner-seat-included'
    | 'family-invites-independent'
    | 'duplicate-claim-protection'
    | 'oversell-protection'
    | 'family-binding-preserved';
  title: string;
  required: true;
  status: SponsoredSeatEvidenceStatus;
  explanation: string;
}

export interface SponsoredSeatCompletionGate {
  title: string;
  subsystem: 'membership';
  authorityDomain: 'sponsored-seat-lifecycle';
  decision: SponsoredSeatCompletionDecision;
  status: SponsoredSeatEvidenceStatus;
  completionAllowed: boolean;
  requiredCheckCount: number;
  passedCheckCount: number;
  failedCheckCount: number;
  unverifiedCheckCount: number;
  failedChecks: SponsoredSeatCompletionCheck[];
  unverifiedChecks: SponsoredSeatCompletionCheck[];
  checks: SponsoredSeatCompletionCheck[];
  constitutionalRule: string;
  nextMilestone: string;
  evaluatedAt: string;
}

export function evaluateSponsoredSeatCompletionGate():
  SponsoredSeatCompletionGate {
  const evidence = projectSponsoredSeatRuntimeEvidence();

  const checks: SponsoredSeatCompletionCheck[] = [
    {
      id: 'schema-activation',
      title: 'Sponsored seat schema activation',
      required: true,
      status:
        evidence.milestones.find(
          milestone => milestone.milestone === 'MAE-002A',
        )?.status ?? 'UNVERIFIED',
      explanation:
        'The invite lifecycle schema and sponsored-seat identity fields must be active.',
    },
    {
      id: 'reservation-authority',
      title: 'Sponsored seat reservation authority',
      required: true,
      status:
        evidence.milestones.find(
          milestone => milestone.milestone === 'MAE-002B',
        )?.status ?? 'UNVERIFIED',
      explanation:
        'Seat capacity, reservation expiry, and owner counting must be database-enforced.',
    },
    {
      id: 'claim-return-authority',
      title: 'Sponsored seat claim and return authority',
      required: true,
      status:
        evidence.milestones.find(
          milestone => milestone.milestone === 'MAE-002C',
        )?.status ?? 'UNVERIFIED',
      explanation:
        'Authenticated claims, returns, expiry, and reuse protection must be active.',
    },
    {
      id: 'runtime-evidence-projection',
      title: 'Membership Governor runtime evidence projection',
      required: true,
      status: evidence.status,
      explanation:
        'The certified sponsored-seat authority must be visible in the Command Center.',
    },
    {
      id: 'owner-seat-included',
      title: 'Owner seat included in all plan capacities',
      required: true,
      status: evidence.ownerSeatIncluded
        ? 'PASS'
        : 'FAIL',
      explanation:
        'The membership owner permanently occupies one total plan seat.',
    },
    {
      id: 'family-invites-independent',
      title: 'Family invitations remain independent',
      required: true,
      status:
        evidence.familyInvitesUnlimited &&
        !evidence.familyInviteConsumesSeat
          ? 'PASS'
          : 'FAIL',
      explanation:
        'Family-profile invitations must remain unlimited and must not consume sponsored seats.',
    },
    {
      id: 'duplicate-claim-protection',
      title: 'Duplicate claim protection',
      required: true,
      status:
        evidence.duplicateClaimDenied &&
        evidence.sameClaimantRetryIdempotent
          ? 'PASS'
          : 'FAIL',
      explanation:
        'A second account must be denied while the original claimant may retry safely.',
    },
    {
      id: 'oversell-protection',
      title: 'Concurrent oversell protection',
      required: true,
      status: evidence.concurrentOversellPrevented
        ? 'PASS'
        : 'FAIL',
      explanation:
        'Concurrent sponsored reservations must not exceed plan capacity.',
    },
    {
      id: 'family-binding-preserved',
      title: 'Family-tree binding preserved',
      required: true,
      status:
        evidence.familyTreeBindingPreserved &&
        evidence.membershipIndependentFromFamilyRelationship
          ? 'PASS'
          : 'FAIL',
      explanation:
        'Membership ownership must remain independent from permanent family identity.',
    },
  ];

  const passedCheckCount = checks.filter(
    check => check.status === 'PASS',
  ).length;

  const failedChecks = checks.filter(
    check => check.status === 'FAIL',
  );

  const unverifiedChecks = checks.filter(
    check => check.status === 'UNVERIFIED',
  );

  const completionAllowed =
    failedChecks.length === 0 &&
    unverifiedChecks.length === 0 &&
    passedCheckCount === checks.length;

  const decision: SponsoredSeatCompletionDecision =
    completionAllowed
      ? 'COMPLETE'
      : failedChecks.length > 0
        ? 'BLOCKED'
        : 'UNVERIFIED';

  const status: SponsoredSeatEvidenceStatus =
    decision === 'COMPLETE'
      ? 'PASS'
      : decision === 'BLOCKED'
        ? 'FAIL'
        : 'UNVERIFIED';

  return Object.freeze({
    title: 'Sponsored Seat Authority Completion Gate',
    subsystem: 'membership',
    authorityDomain: 'sponsored-seat-lifecycle',
    decision,
    status,
    completionAllowed,
    requiredCheckCount: checks.length,
    passedCheckCount,
    failedCheckCount: failedChecks.length,
    unverifiedCheckCount: unverifiedChecks.length,
    failedChecks,
    unverifiedChecks,
    checks,
    constitutionalRule:
      'Family identity is permanent. Capabilities are licensed. Resources are governed.',
    nextMilestone:
      'MAE-003 — Unsponsored Family Access Authority',
    evaluatedAt: new Date().toISOString(),
  });
}

export const SPONSORED_SEAT_COMPLETION_GATE_READY =
  true;
